<template>
  <div class="page-shell case-library-page case-tech-page">

    <!-- ══════════════════════════════
         HERO — 顶部资源库头图区
    ══════════════════════════════ -->
    <section class="case-hero">
      <!-- 背景装饰 -->
      <div class="hero-grid-bg"></div>
      <div class="hero-glow hero-glow-l"></div>
      <div class="hero-glow hero-glow-r"></div>

      <div class="hero-inner">
        <!-- 左侧文案 -->
        <div class="hero-copy">
          <div class="hero-eyebrow">
            <span class="eyebrow-dot"></span>
            Classic Case Library
          </div>
          <h1>经典案例<span class="h1-en">资源库</span></h1>
          <p class="hero-sub">
            情景化案例资源库 · 支持在线浏览、案例学习与课程化训练
          </p>
          <p class="hero-desc">
            汇聚典型情景学习案例，面向情景学习、案例训练与课程组织。保持原有后台接口不变，通过前端展示增强案例检索、分类浏览和进入学习体验。
          </p>
          <div class="hero-actions">
            <button class="btn-primary" @click="scrollToList">
              <i class="el-icon-collection"></i>浏览案例
            </button>
            <button class="btn-ghost" @click="fetchReviewingCourses">
              <i class="el-icon-refresh"></i>刷新资源
            </button>
          </div>
        </div>

        <!-- 右侧状态面板 -->
        <div class="hero-panel">
          <div class="hp-header">
            <span class="hp-dot"></span>
            资源状态面板
          </div>
          <div class="hp-metrics">
            <div class="hp-metric-item">
              <span class="hp-metric-label">当前展示</span>
              <strong class="hp-metric-val">{{ filteredCourses.length }}</strong>
            </div>
            <div class="hp-divider"></div>
            <div class="hp-metric-item">
              <span class="hp-metric-label">资源总量</span>
              <strong class="hp-metric-val">{{ reviewingTotal || reviewingCourses.length }}</strong>
            </div>
          </div>
          <div class="hp-status-list">
            <div class="hp-status-row" v-for="s in panelStatus" :key="s.name">
              <span :class="['hp-status-dot', s.dotClass]"></span>
              <span class="hp-status-name">{{ s.name }}</span>
              <span class="hp-status-tag">{{ s.tag }}</span>
            </div>
          </div>
          <div class="hp-tags">
            <span>情景学习</span>
            <span>案例训练</span>
            <span>复盘提升</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ══════════════════════════════
         工具栏 + 列表区
    ══════════════════════════════ -->
    <section class="page-container" ref="caseList">

      <!-- 搜索 + 分类 Toolbar -->
      <div class="library-toolbar">
        <div class="toolbar-inner">
          <div class="search-box">
            <i class="el-icon-search search-icon"></i>
            <input
              v-model="keyword"
              placeholder="搜索案例名称或摘要"
              class="search-input"
            />
            <button
              v-if="keyword"
              @click="keyword = ''"
              class="search-clear"
              type="button"
            >
              <i class="el-icon-close"></i>
            </button>
          </div>
          <div class="category-tabs">
            <button
              v-for="category in categories"
              :key="category"
              :class="['cat-btn', { 'cat-btn--active': activeCategory === category }]"
              @click="activeCategory = category"
            >
              {{ category }}
            </button>
          </div>
        </div>
      </div>

      <!-- 列表头部 -->
      <div class="list-header">
        <div class="list-header-left">
          <span class="section-kicker">Scenario Resources</span>
          <h2 class="list-title">案例列表</h2>
          <p class="list-desc">共 {{ filteredCourses.length }} 个可展示案例，点击卡片进入学习页面。</p>
        </div>
        <div class="list-count-badge">
          <span class="lcb-label">全部案例</span>
          <strong class="lcb-num">{{ filteredCourses.length }}</strong>
        </div>
      </div>

      <!-- 案例卡片网格 -->
      <div v-if="filteredCourses.length" class="case-grid">
        <article
          class="case-card"
          v-for="(course, index) in filteredCourses"
          :key="course.id || course.urlCode || course.title || index"
          @click="handleDetail(course)"
        >
          <!-- 封面区 -->
          <div class="card-cover">
            <img
              v-if="course.imgUrl && !imageErrors[index]"
              :src="baseUrl + course.imgUrl"
              :alt="course.title || '案例封面'"
              @error="markImageError(index)"
              class="cover-img"
            />
            <!-- 高级占位封面 -->
            <div v-else class="cover-fallback">
              <div class="fallback-icon-wrap">
                <i class="el-icon-film"></i>
              </div>
              <span class="fallback-label">情景案例</span>
              <div class="fallback-grid"></div>
            </div>

            <!-- 渐变遮罩 -->
            <div class="cover-mask"></div>

            <!-- 左下角类型标签 -->
            <span class="tag-type">{{ guessCategory(course, index) }}</span>

            <!-- 右上角状态标签 -->
            <span :class="['tag-status', index < 3 ? 'tag-status--hot' : 'tag-status--normal']">
              {{ index < 3 ? '推荐' : '资源' }}
            </span>
          </div>

          <!-- 内容区 -->
          <div class="card-body">
            <h3 class="card-title">{{ course.title || '未命名案例' }}</h3>
            <p class="card-desc">{{ course.summary || '围绕典型情景开展案例学习、任务理解和训练复盘。' }}</p>
            <div class="card-tags">
              <span>情景学习</span>
              <span>任务理解</span>
              <span>在线训练</span>
            </div>
            <div class="card-action">
              <span>进入学习</span>
              <i class="el-icon-right"></i>
            </div>
          </div>

          <!-- hover 时显示的顶部光条 -->
          <div class="card-glow-bar"></div>
        </article>
      </div>

      <!-- 空状态 -->
      <div v-else class="empty-state">
        <div class="empty-icon-wrap">
          <i class="el-icon-folder-opened"></i>
        </div>
        <h3>暂无匹配案例</h3>
        <p>可尝试清空搜索条件、切换分类，或刷新案例列表。</p>
        <button class="btn-ghost" @click="keyword = ''; activeCategory = '全部案例'">
          <i class="el-icon-refresh"></i>重置筛选
        </button>
      </div>

      <!-- 分页 -->
      <div class="pagination-wrap" v-if="reviewingTotal > reviewingPerPage">
        <Pagination
          :total-items="reviewingTotal"
          :items-per-page="reviewingPerPage"
          :current-page="reviewingPage"
          @page-changed="changeReviewingPage"
        />
      </div>

    </section>
  </div>
</template>

<script>
import Pagination from "@/components/Pagination";
import { getPublicSubjectList } from "@/api/index";
import { mapState } from "vuex";

export default {
  name: "More",
  components: {
    Pagination,
  },
  data() {
    return {
      keyword: "",
      activeCategory: "全部案例",
      categories: ["全部案例", "应急处置", "公共安全", "医疗救援", "交通安全", "内部验证"],
      reviewingCourses: [],
      reviewingTotal: 0,
      reviewingPerPage: 100,
      reviewingPage: 1,
      imageErrors: {},
      panelStatus: [
        { name: "在线学习",  tag: "可进入", dotClass: "dot-green"  },
        { name: "情景体验",  tag: "已就绪", dotClass: "dot-blue"   },
        { name: "持续更新",  tag: "同步中", dotClass: "dot-orange" },
      ],
    };
  },
  computed: {
    ...mapState(["baseUrl"]),
    filteredCourses() {
      const keyword = this.keyword.trim().toLowerCase();
      return this.reviewingCourses.filter((course, index) => {
        const text = `${course.title || ""}${course.summary || ""}`.toLowerCase();
        const keywordMatched = !keyword || text.includes(keyword);
        if (!keywordMatched) return false;
        if (this.activeCategory === "全部案例") return true;
        return this.guessCategory(course, index) === this.activeCategory;
      });
    },
  },
  mounted() {
    this.fetchReviewingCourses();
  },
  methods: {
    fetchReviewingCourses() {
      getPublicSubjectList({
        pageNum: this.reviewingPage,
        pageSize: this.reviewingPerPage,
      })
        .then((response) => {
          if (response.code == 200) {
            this.reviewingTotal = response.total;
            this.reviewingCourses = response.rows || [];
            this.imageErrors = {};
          }
        })
        .catch((error) => {
          console.error("获取公开案例失败:", error);
          this.reviewingCourses = [];
        });
    },
    changeReviewingPage(page) {
      this.reviewingPage = page;
      this.fetchReviewingCourses();
    },
    handleDetail(course) {
      if (!course || !course.urlCode) return;
      this.$router.push({
        name: "StudentClass",
        params: { id: course.urlCode },
      });
    },
    markImageError(index) {
      this.$set(this.imageErrors, index, true);
    },
    scrollToList() {
      if (this.$refs.caseList) {
        this.$refs.caseList.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    guessCategory(course, index) {
      const text = `${course.title || ""}${course.summary || ""}`;
      if (/医疗|医学|检伤|伤员|救护|卫生/.test(text)) return "医疗救援";
      if (/交通|道路|车辆|隧道|机场|航空|马拉松/.test(text)) return "交通安全";
      if (/内部|验证|测试/.test(text)) return "内部验证";
      if (/消防|火灾|森林|危化|救援|应急/.test(text)) return "应急处置";
      return index % 3 === 0 ? "公共安全" : "应急处置";
    },
  },
};
</script>

<style scoped lang="scss">
/* ─────────────────────────────────────────
   Local design tokens
───────────────────────────────────────── */
.case-tech-page {
  --bd:       #081c42;
  --bm:       #1557d8;
  --ba:       #1e9bff;
  --cyan:     #20c8ff;
  --gray:     #475569;
  --gray-s:   #64748b;
  --card-bg:  rgba(255,255,255,0.92);
  --card-bdr: rgba(168,199,245,0.68);
  background:
    radial-gradient(ellipse at 8% 0%,  rgba(32,200,255,0.10), transparent 30%),
    radial-gradient(ellipse at 90% 6%, rgba(47,111,237,0.13), transparent 32%),
    linear-gradient(180deg, #f2f7ff 0%, #f5f8fd 48%, #ffffff 100%);
}

/* ─────────────────────────────────────────
   PAGE CONTAINER
───────────────────────────────────────── */
.page-shell { overflow: hidden; }

.page-container {
  width: min(1400px, calc(100% - 48px));
  margin: 0 auto;
  padding: 48px 0 72px;
}

/* ─────────────────────────────────────────
   HERO
───────────────────────────────────────── */
.case-hero {
  position: relative;
  padding: 52px 0 44px;
  overflow: hidden;
}

.hero-grid-bg {
  position: absolute;
  inset: 0;
  opacity: 0.44;
  background-image:
    linear-gradient(rgba(47,111,237,0.08) 1px, transparent 1px),
    linear-gradient(90deg, rgba(47,111,237,0.08) 1px, transparent 1px);
  background-size: 46px 46px;
  mask-image: linear-gradient(180deg, rgba(0,0,0,0.85), transparent 94%);
  pointer-events: none;
}

.hero-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(16px);
  pointer-events: none;
}
.hero-glow-l {
  width: 420px; height: 420px;
  left: -140px; bottom: -140px;
  background: rgba(21,87,216,0.10);
}
.hero-glow-r {
  width: 280px; height: 280px;
  right: 4%; top: 4%;
  background: rgba(32,200,255,0.10);
}

.hero-inner {
  position: relative;
  z-index: 1;
  width: min(1400px, calc(100% - 48px));
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 36px;
  align-items: end;
}

/* Hero eyebrow */
.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(21,87,216,0.09);
  color: var(--bm);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  margin-bottom: 16px;
}
.eyebrow-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 0 4px rgba(32,200,255,0.20), 0 0 12px rgba(32,200,255,0.7);
}

/* Hero h1 */
.hero-copy h1 {
  margin: 0 0 10px;
  color: var(--bd);
  font-size: clamp(28px, 3.4vw, 44px);
  font-weight: 950;
  line-height: 1.1;
  letter-spacing: -0.8px;
}
.h1-en {
  margin-left: 6px;
  color: var(--bm);
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0px;
  vertical-align: baseline;
}

.hero-sub {
  color: var(--bm);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1.2px;
  opacity: 0.82;
  margin-bottom: 8px;
}

.hero-desc {
  max-width: 600px;
  color: var(--gray);
  font-size: 14px;
  line-height: 1.82;
}

/* Hero buttons */
.hero-actions {
  display: flex;
  gap: 12px;
  margin-top: 22px;
  flex-wrap: wrap;
}
.btn-primary,
.btn-ghost {
  height: 38px;
  padding: 0 18px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 800;
  transition: all 0.22s ease;
}
.btn-primary {
  color: #fff;
  background: linear-gradient(135deg, #1248c8, var(--ba) 62%, var(--cyan));
  box-shadow: 0 10px 24px rgba(21,87,216,0.24);
}
.btn-primary:hover { transform: translateY(-2px); box-shadow: 0 14px 30px rgba(21,87,216,0.32); }
.btn-ghost {
  color: var(--bd);
  background: rgba(255,255,255,0.86);
  border: 1px solid rgba(21,87,216,0.16);
  backdrop-filter: blur(10px);
}
.btn-ghost:hover { transform: translateY(-2px); border-color: var(--ba); }

/* ── Hero Panel (right side) ── */
.hero-panel {
  position: relative;
  padding: 20px;
  border-radius: 20px;
  background: linear-gradient(150deg, rgba(8,22,58,0.96), rgba(10,52,112,0.92));
  border: 1px solid rgba(32,200,255,0.20);
  color: #fff;
  box-shadow: 0 24px 52px rgba(4,14,44,0.20);
  overflow: hidden;
}
/* subtle inner glow */
.hero-panel::after {
  content: "";
  position: absolute;
  right: -50px; top: -50px;
  width: 150px; height: 150px;
  border-radius: 50%;
  background: rgba(32,200,255,0.14);
  filter: blur(4px);
  pointer-events: none;
}
.hp-header {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 800;
  color: rgba(255,255,255,0.65);
  margin-bottom: 16px;
  letter-spacing: 0.3px;
}
.hp-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 8px rgba(32,200,255,0.9);
}
.hp-metrics {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255,255,255,0.09);
}
.hp-metric-item {
  flex: 1;
  text-align: center;
}
.hp-metric-label {
  display: block;
  font-size: 11px;
  color: rgba(255,255,255,0.60);
  margin-bottom: 4px;
  font-weight: 700;
}
.hp-metric-val {
  font-size: 28px;
  font-weight: 950;
  color: #fff;
  line-height: 1;
}
.hp-divider {
  width: 1px;
  height: 40px;
  background: rgba(255,255,255,0.12);
}
.hp-status-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}
.hp-status-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}
.hp-status-dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-green  { background: #4ade80; box-shadow: 0 0 6px rgba(74,222,128,0.8); }
.dot-blue   { background: #60a5fa; box-shadow: 0 0 6px rgba(96,165,250,0.8); }
.dot-orange { background: #fbbf24; box-shadow: 0 0 6px rgba(251,191,36,0.8); }
.hp-status-name { flex: 1; color: rgba(255,255,255,0.82); }
.hp-status-tag  { color: rgba(255,255,255,0.46); font-size: 11px; }
.hp-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 14px;
  border-top: 1px solid rgba(255,255,255,0.09);
}
.hp-tags span {
  padding: 4px 9px;
  border-radius: 999px;
  color: #c4f0ff;
  background: rgba(255,255,255,0.08);
  font-size: 11px;
  font-weight: 800;
  border: 1px solid rgba(255,255,255,0.10);
}

/* ─────────────────────────────────────────
   TOOLBAR
───────────────────────────────────────── */
.library-toolbar {
  margin-bottom: 24px;
  padding: 16px 20px;
  border-radius: 18px;
  background: rgba(255,255,255,0.90);
  border: 1px solid var(--card-bdr);
  box-shadow: 0 10px 28px rgba(8,28,66,0.07);
  backdrop-filter: blur(12px);
}
.toolbar-inner {
  display: grid;
  grid-template-columns: minmax(240px, 400px) 1fr;
  gap: 16px;
  align-items: center;
}

.search-box {
  height: 40px;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 12px;
  border-radius: 12px;
  background: #f4f8fe;
  border: 1px solid rgba(21,87,216,0.12);
  transition: border-color 0.2s;
}
.search-box:focus-within { border-color: var(--ba); box-shadow: 0 0 0 3px rgba(30,155,255,0.10); }
.search-icon { color: var(--ba); font-size: 16px; flex-shrink: 0; }
.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: var(--bd);
  font-size: 13px;
}
.search-clear {
  width: 22px; height: 22px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: var(--gray-s);
  background: rgba(21,87,216,0.08);
  display: grid;
  place-items: center;
  font-size: 12px;
  transition: background 0.2s;
}
.search-clear:hover { background: rgba(21,87,216,0.16); }

.category-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  justify-content: flex-end;
}
.cat-btn {
  height: 32px;
  padding: 0 12px;
  border: 1px solid rgba(21,87,216,0.14);
  border-radius: 999px;
  color: var(--gray);
  background: transparent;
  cursor: pointer;
  transition: all 0.20s ease;
  font-size: 12px;
  font-weight: 800;
}
.cat-btn:hover {
  color: var(--bm);
  border-color: rgba(21,87,216,0.38);
  background: rgba(21,87,216,0.05);
}
.cat-btn--active {
  color: #fff;
  background: linear-gradient(135deg, #1248c8, var(--ba));
  border-color: transparent;
  box-shadow: 0 6px 16px rgba(21,87,216,0.20);
}

/* ─────────────────────────────────────────
   LIST HEADER
───────────────────────────────────────── */
.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}
.section-kicker {
  display: inline-block;
  margin-bottom: 5px;
  color: var(--ba);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
}
.list-title {
  color: var(--bd);
  font-size: 22px;
  font-weight: 950;
  margin: 0 0 4px;
  line-height: 1.2;
}
.list-desc {
  color: var(--gray-s);
  font-size: 13px;
}
.list-count-badge {
  min-width: 96px;
  padding: 12px 14px;
  border-radius: 16px;
  text-align: center;
  background: rgba(255,255,255,0.82);
  border: 1px solid var(--card-bdr);
  box-shadow: 0 8px 22px rgba(8,28,66,0.07);
}
.lcb-label {
  display: block;
  color: var(--gray-s);
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 4px;
}
.lcb-num {
  color: var(--bm);
  font-size: 26px;
  font-weight: 950;
  line-height: 1;
}

/* ─────────────────────────────────────────
   CASE GRID & CARDS
───────────────────────────────────────── */
.case-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}

/* Individual card */
.case-card {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  background: var(--card-bg);
  border: 1px solid var(--card-bdr);
  box-shadow: 0 10px 28px rgba(8,28,66,0.07);
  cursor: pointer;
  transition: transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease;
}
.case-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 56px rgba(8,28,66,0.12);
  border-color: rgba(30,155,255,0.38);
}

/* Top glow bar (revealed on hover) */
.card-glow-bar {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 2px;
  background: linear-gradient(90deg, var(--bm), var(--cyan), rgba(32,200,255,0));
  opacity: 0;
  transition: opacity 0.24s ease;
}
.case-card:hover .card-glow-bar { opacity: 1; }

/* ── Cover ── */
.card-cover {
  position: relative;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, #e8f0fc, #f4f8ff);
}
.cover-img {
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.36s ease;
}
.case-card:hover .cover-img { transform: scale(1.05); }

/* Premium fallback */
.cover-fallback {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  overflow: hidden;
}
.fallback-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(21,87,216,0.09) 1px, transparent 1px),
    linear-gradient(90deg, rgba(21,87,216,0.09) 1px, transparent 1px);
  background-size: 20px 20px;
  pointer-events: none;
}
.fallback-icon-wrap {
  position: relative;
  z-index: 1;
  width: 56px; height: 56px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(21,87,216,0.14), rgba(32,200,255,0.18));
  border: 1px solid rgba(21,87,216,0.18);
  color: var(--bm);
  font-size: 26px;
  box-shadow: 0 10px 26px rgba(21,87,216,0.14);
}
.fallback-label {
  position: relative;
  z-index: 1;
  font-size: 13px;
  font-weight: 800;
  color: var(--bm);
  letter-spacing: 0.5px;
}

/* Cover gradient mask */
.cover-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, transparent 40%, rgba(8,20,52,0.40));
  pointer-events: none;
}

/* Badges */
.tag-type,
.tag-status {
  position: absolute;
  z-index: 3;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.2px;
}
.tag-type {
  left: 12px; bottom: 12px;
  padding: 4px 9px;
  color: #fff;
  background: rgba(18,72,200,0.90);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,0.14);
}
.tag-status {
  right: 12px; top: 12px;
  padding: 4px 9px;
}
.tag-status--hot {
  color: #fff;
  background: linear-gradient(135deg, #f97316, #fbbf24);
  box-shadow: 0 4px 12px rgba(249,115,22,0.28);
}
.tag-status--normal {
  color: #c4f0ff;
  background: rgba(8,28,66,0.66);
  border: 1px solid rgba(255,255,255,0.14);
  backdrop-filter: blur(8px);
}

/* ── Card body ── */
.card-body {
  padding: 16px 18px 14px;
}
.card-title {
  color: var(--bd);
  font-size: 16px;
  font-weight: 900;
  line-height: 1.38;
  margin: 0 0 8px;
  min-height: 44px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.card-desc {
  color: var(--gray);
  font-size: 13px;
  line-height: 1.72;
  min-height: 60px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}
.card-tags span {
  padding: 3px 8px;
  border-radius: 999px;
  color: var(--bm);
  background: rgba(21,87,216,0.07);
  border: 1px solid rgba(21,87,216,0.13);
  font-size: 11px;
  font-weight: 800;
}
.card-action {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(226,232,240,0.8);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--bm);
  font-size: 13px;
  font-weight: 900;
  transition: color 0.2s;
}
.case-card:hover .card-action { color: var(--ba); }

/* ─────────────────────────────────────────
   EMPTY STATE
───────────────────────────────────────── */
.empty-state {
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 32px;
  background: rgba(255,255,255,0.88);
  border: 1px solid var(--card-bdr);
  border-radius: 20px;
  box-shadow: 0 10px 28px rgba(8,28,66,0.06);
}
.empty-icon-wrap {
  width: 64px; height: 64px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, rgba(21,87,216,0.10), rgba(32,200,255,0.12));
  color: var(--ba);
  font-size: 28px;
  margin-bottom: 16px;
}
.empty-state h3 {
  color: var(--bd);
  font-size: 18px;
  font-weight: 900;
  margin-bottom: 8px;
}
.empty-state p {
  color: var(--gray-s);
  font-size: 13px;
  line-height: 1.72;
  margin-bottom: 20px;
}

/* ─────────────────────────────────────────
   PAGINATION
───────────────────────────────────────── */
.pagination-wrap {
  margin-top: 32px;
  display: flex;
  justify-content: center;
}

/* ─────────────────────────────────────────
   RESPONSIVE
───────────────────────────────────────── */
@media (max-width: 1100px) {
  .case-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 960px) {
  .hero-inner,
  .toolbar-inner {
    grid-template-columns: 1fr;
  }
  .hero-panel { display: none; }
  .category-tabs { justify-content: flex-start; }
}

@media (max-width: 640px) {
  .case-grid { grid-template-columns: 1fr; }
  .list-header { display: block; }
  .list-count-badge { margin-top: 14px; text-align: left; width: fit-content; }
  .page-container { padding: 32px 0 52px; }
  .case-hero { padding: 36px 0 30px; }
}
</style>
